import React, { useCallback, useEffect, useState } from 'react';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import { getFavoriteAssets, removeFavoriteAsset } from '@/shared/lib/storage';
import type { AssetListAction, FavoriteAssetItem } from '@/shared/types/assetList';
import { AssetList } from '@/shared/ui/AssetList';
import { Icon } from '@/shared/ui/Icon';
import { NoDataBoundary } from '@/shared/ui/NoDataBoundary';

import type { FavoriteAsset } from '@/entities/asset';

import styles from './FavoritesTab.module.css';

dayjs.extend(relativeTime);

type FavoritesTabProps = {
  onAssetClick: (asset: FavoriteAssetItem) => void;
};

export const FavoritesTab: React.FC<FavoritesTabProps> = ({ onAssetClick }) => {
  const [favoriteAssets, setFavoriteAssets] = useState<FavoriteAssetItem[]>([]);

  const loadFavoriteAssets = useCallback(() => {
    const favorites = getFavoriteAssets();

    const favoriteItems: FavoriteAssetItem[] = favorites.map((asset: FavoriteAsset) => {
      return {
        id: asset.id,
        name: asset.name,
        type: asset.type,
        addedAt: asset.addedAt,
        fqn: asset.fqn,
      };
    });
    setFavoriteAssets(favoriteItems);
  }, []);

  useEffect(() => {
    loadFavoriteAssets();

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'cyclone-favorite-assets') {
        loadFavoriteAssets();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [loadFavoriteAssets]);

  const handleRemoveFromFavorites = (assetId: string) => {
    removeFavoriteAsset(assetId);
    setFavoriteAssets((prev) => prev.filter((asset) => asset.id !== assetId));
  };

  const getActions = (item: FavoriteAssetItem): AssetListAction[] => [
    {
      key: 'remove',
      icon: <Icon name="star-filled" size={16} strokeWidth={1.5} />,
      onClick: () => handleRemoveFromFavorites(item.id),
      className: styles.deleteButton,
    },
  ];

  return (
    <div className={styles.tabWrapper}>
      <NoDataBoundary
        data={favoriteAssets}
        emptyDescription="즐겨찾기 항목이 없습니다. 자주 사용하는 데이터 자산을 즐겨찾기하면 여기에 표시됩니다">
        <div className={styles.tabContent}>
          <div className={styles.listContainer}>
            <AssetList
              items={favoriteAssets}
              getActions={getActions}
              getMetadata={(item) => dayjs(item.addedAt).fromNow()}
              onItemClick={onAssetClick}
            />
          </div>
        </div>
      </NoDataBoundary>
    </div>
  );
};
